import {
  Grid,
  TextField,
  Paper,
  Button,
  CircularProgress,
  Snackbar,
  Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Download } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Pagination } from '@mui/material';
import { DateRange } from '../../types/dateRange';
import { Moment } from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';

import DateRangePicker from '../../components/DateRangePicker';
import ScootersLockHistoryTable from '../../components/ScootersLockHistoryTable';
import Select from '../../components/Select';
import { SelectOption } from '../../components/Select/interface';
import Title from '../../components/Title';
import { useGetAdminsQuery } from '../../redux/api/adminsApi';
import {
  scootersApi,
  useGetScootersLockHistoryQuery
} from '../../redux/api/scootersApi';
import { ScootersLockHistoryItem } from '../../resources/types/scootersTypes';
import {
  buildLockHistoryCsv,
  downloadCsv,
  getExportDateStamp
} from './lockHistoryCsv';
import { InputChangeEvent } from '../../resources/utils';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuth } from '../../redux/slices/authSlice';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import { selectCities } from '../../redux/slices/citiesSlice';
import { AppDispatch } from '../../redux/store';

// Розмір сторінки та стеля запитів при вивантаженні всієї історії
const EXPORT_PAGE_LIMIT = 500;
const EXPORT_MAX_PAGES = 200;

const ScootersLockHistoryPage = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { data: admins } = useGetAdminsQuery();

  const citiesList = useSelector(selectCities);
  const adminInfo = useSelector(selectAdmins);
  const userCityId = useMemo(() => {
    const city = citiesList.items.find(item => item._id === adminInfo.currentCity);
    if (city) {
      return city._id;
    }
  }, [adminInfo.currentCity, citiesList.items]);

  const [adminId, setAdminId] = useState('');
  const [scooterName, setScooterName] = useState('');
  const [scooterId, setScooterId] = useState('');
  const [dateRange, setDateRange] = useState<DateRange<Moment | null>>([
    null,
    null
  ]);
  const [page, setPage] = useState(1);

  const clearPageWrapper = useCallback(
    <T extends (...args: any[]) => void>(fn: T) => {
      return (...args: Parameters<T>) => {
        fn(...args);
        setPage(1);
      };
    },
    []
  );

  const queryParams = useMemo(() => ({
    page,
    ...(adminId && { adminId }),
    city: userCityId,
    type: 'admin,driver',
    ...(dateRange[0] && { from: dateRange[0].toISOString() }),
    ...(dateRange[1] && { to: dateRange[1].toISOString() }),
    ...(scooterName && { scooterName })
  }), [page, adminId, userCityId, dateRange, scooterName]);

  const { data: scootersLockHistory, isFetching } = useGetScootersLockHistoryQuery(
    queryParams,
    {
      skip: !userCityId,
      refetchOnMountOrArgChange: true
    }
  );

  useEffect(() => {
    console.log('Query params:', queryParams);
    console.log('Data:', scootersLockHistory);
  }, [queryParams, scootersLockHistory]);

  const dispatch = useDispatch<AppDispatch>();
  const [isExporting, setIsExporting] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const adminsSelectOptions = useMemo<SelectOption[]>(
    () =>
      admins
        ? [
            {
              value: '',
              label: t('lockHistory.all')
            },
            ...admins.map(admin => ({
              value: admin._id,
              label: admin.login
            }))
          ]
        : [],
    [admins, t]
  );

  const selectedAdminLogin = useMemo(
    () => admins?.find(admin => admin._id === adminId)?.login ?? adminId,
    [admins, adminId]
  );

  // Вивантажує всю історію вибраного працівника за весь період (без фільтрів дат/самоката)
  const handleExportCsv = useCallback(async () => {
    if (!adminId || !userCityId || isExporting) return;

    setIsExporting(true);

    try {
      const rows: ScootersLockHistoryItem[] = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        // RTK 1.6 не має .unwrap() у lazy-запитів, тому диспатчимо initiate напряму
        const request = dispatch(
          scootersApi.endpoints.getScootersLockHistoryPage.initiate(
            {
              page: currentPage,
              limit: EXPORT_PAGE_LIMIT,
              adminId,
              city: userCityId,
              type: 'admin,driver'
            },
            { forceRefetch: true }
          )
        );

        try {
          const { data, error } = await request;

          if (error) throw error;
          if (!data) break;

          rows.push(...data.lockHistory);
          totalPages = data.pagination?.totalPages || 1;
        } finally {
          request.unsubscribe();
        }

        currentPage += 1;
      } while (currentPage <= totalPages && currentPage <= EXPORT_MAX_PAGES);

      if (!rows.length) {
        setSnackbarMessage(t('lockHistory.csvNoData'));
        return;
      }

      rows.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      const fileNamePart = selectedAdminLogin.replace(/[^\w.-]+/g, '_');
      downloadCsv(
        buildLockHistoryCsv(rows, t),
        `lock_history_${fileNamePart}_${getExportDateStamp()}.csv`
      );
      setSnackbarMessage(t('lockHistory.csvSuccess', { total: rows.length }));
    } catch (error) {
      console.error('Failed to export lock history:', error);
      setSnackbarMessage(t('lockHistory.csvError'));
    } finally {
      setIsExporting(false);
    }
  }, [
    adminId,
    userCityId,
    isExporting,
    dispatch,
    selectedAdminLogin,
    t
  ]);

  const handleScooterName = (e: InputChangeEvent) => {
    setScooterName(e.target.value);
  };

  useEffect(() => {
    if (scootersLockHistory && scooterName) {
      const history = [
        ...scootersLockHistory.locked,
        ...scootersLockHistory.unlocked
      ];

      const scooterId = history.find(
        scooter => scooter.scooterId?.name === scooterName
      )?.scooterId._id;

      if (scooterId) setScooterId(scooterId);
    }

    if (!scooterName) setScooterId('');
  }, [scooterId, scooterName, scootersLockHistory]);

  if (!scootersLockHistory) {
    return null;
  }

  return (
    <Grid container direction='column' spacing={4} sx={{ opacity: isFetching ? 0.5 : 1, transition: 'opacity 0.2s' }}>
      <Grid item>
        <Title>{t('lockHistory.title')}</Title>
      </Grid>
      <Grid item>
        <Paper sx={{ border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', borderRadius: 2, padding: '20px' }}>
          <Grid container spacing={2}>
            {
              adminsSelectOptions?.length ? (
                <Grid item>
                <Select
                  style={{ minWidth: 140 }}
                  fullWidth
                  label={t('lockHistory.userFilter')}
                  options={adminsSelectOptions}
                  onChange={clearPageWrapper(setAdminId)}
                  value={adminId}
                />
              </Grid>
              ) : null
            }
            <Grid item>
              <DateRangePicker
                startText={t('lockHistory.from')}
                endText={t('lockHistory.to')}
                value={dateRange}
                onChange={(date: DateRange<Moment>) => {
                  date[1]?.add('23:59', 'hours');
                  setDateRange(date);
                  setPage(1);
                }}
              />
            </Grid>
            <Grid item>
              <TextField
                label={t('lockHistory.scooterName')}
                value={scooterName}
                onChange={handleScooterName}
              />
            </Grid>
            <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip
                title={
                  adminId
                    ? t('lockHistory.csvExportHint', { user: selectedAdminLogin })
                    : t('lockHistory.csvSelectUser')
                }
              >
                <span>
                  <Button
                    variant='outlined'
                    startIcon={
                      isExporting ? (
                        <CircularProgress size={16} color='inherit' />
                      ) : (
                        <Download />
                      )
                    }
                    onClick={handleExportCsv}
                    disabled={!adminId || isExporting}
                    sx={{
                      borderColor: '#878787',
                      color: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                      '&:hover': { borderColor: '#616161' }
                    }}
                  >
                    {t('lockHistory.csvExport')}
                  </Button>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item>
        <Pagination
          page={page}
          count={scootersLockHistory.pages}
          onChange={(event, page) => setPage(page)}
        />
      </Grid>
      <Grid item>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', borderRadius: 2, padding: '20px' }}>
              <ScootersLockHistoryTable
                isLocked
                rows={scootersLockHistory.locked}
                isScooterId={Boolean(scooterId)}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', borderRadius: 2, padding: '20px' }}>
              <ScootersLockHistoryTable
                isLocked={false}
                rows={scootersLockHistory.unlocked}
                isScooterId={Boolean(scooterId)}
              />
            </Paper>
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <Pagination
          page={page}
          count={scootersLockHistory.pages}
          onChange={(event, page) => setPage(page)}
        />
      </Grid>
      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage('')}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            backgroundColor: theme.palette.mode === 'light' ? '#323232' : '#F5F5F5',
            color: theme.palette.mode === 'light' ? '#FFFFFF' : '#000000',
            borderRadius: '8px',
            fontWeight: 500
          }
        }}
      />
    </Grid>
  );
};

export default ScootersLockHistoryPage;
